import { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getAdsConsent, setAdsConsent, type AdsConsent } from '@/ads';

export const OPEN_ADS_CONSENT_EVENT = 'permiso-vtc:open-ads-consent';

export function openAdsConsent(): void {
  window.dispatchEvent(new Event(OPEN_ADS_CONSENT_EVENT));
}

export function AdsConsentBanner() {
  const [choice, setChoice] = useState<AdsConsent>(() => getAdsConsent());
  const [open, setOpen] = useState(() => getAdsConsent() === null);

  useEffect(() => {
    const requestedByUrl = new URLSearchParams(window.location.search).get('privacidad') === 'medicion';
    if (requestedByUrl) {
      setOpen(true);
      const url = new URL(window.location.href);
      url.searchParams.delete('privacidad');
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    }
    const handleOpen = () => setOpen(true);
    window.addEventListener(OPEN_ADS_CONSENT_EVENT, handleOpen);
    return () => window.removeEventListener(OPEN_ADS_CONSENT_EVENT, handleOpen);
  }, []);

  const select = (next: Exclude<AdsConsent, null>) => {
    setAdsConsent(next);
    setChoice(next);
    setOpen(false);
  };

  if (!open) return null;

  return (
    <aside
      aria-label="Preferencias de medición"
      className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-3xl rounded-2xl border bg-background p-4 shadow-raised sm:bottom-5 sm:p-5"
      role="dialog"
    >
      <div className="flex gap-3">
        <ShieldCheck className="mt-0.5 size-6 shrink-0 text-primary" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold">¿Permites medir la publicidad?</p>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">
            Si aceptas, Google Ads podrá usar cookies e identificadores técnicos para atribuir visitas y compras a nuestros
            anuncios. No activamos publicidad personalizada ni enviamos tu correo. Puedes cambiarlo cuando quieras.{' '}
            <a
              className="underline underline-offset-4"
              href="https://business.safety.google/privacy/"
              rel="nofollow noopener"
              target="_blank"
            >
              Cómo usa Google los datos
            </a>
          </p>
          <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => select('denied')}>
              {choice === 'granted' ? 'Retirar consentimiento' : 'Rechazar'}
            </Button>
            <Button onClick={() => select('granted')}>Permitir medición</Button>
          </div>
        </div>
      </div>
    </aside>
  );
}
